import { useReducer, useEffect, useRef } from "react";
import gsap from "gsap";

interface TypingOptions {
  speed?: number;
  startDelay?: number;
  onComplete?: () => void;
  enabled?: boolean;
}

interface TypingState {
  displayed: string;
  isDone: boolean;
}

type TypingAction =
  | { type: "RESET" }
  | { type: "UPDATE_DISPLAYED"; payload: string }
  | { type: "SET_DONE" };

function typingReducer(state: TypingState, action: TypingAction): TypingState {
  switch (action.type) {
    case "RESET":
      return { displayed: "", isDone: false };
    case "UPDATE_DISPLAYED":
      return { ...state, displayed: action.payload };
    case "SET_DONE":
      return { ...state, isDone: true };
    default:
      return state;
  }
}

export function useTypingEffect(text: string, options: TypingOptions = {}) {
  const { speed = 50, startDelay = 0, onComplete, enabled = true } = options;
  const [state, dispatch] = useReducer(typingReducer, {
    displayed: "",
    isDone: false,
  });
  const indexRef = useRef(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    dispatch({ type: "RESET" });
    indexRef.current = 0;
    cancelledRef.current = false;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReduced) {
      dispatch({ type: "UPDATE_DISPLAYED", payload: text });
      dispatch({ type: "SET_DONE" });
      return;
    }

    const delayTimer = gsap.delayedCall(startDelay / 1000, () => {
      const type = () => {
        if (cancelledRef.current) return;
        if (indexRef.current < text.length) {
          indexRef.current++;
          dispatch({
            type: "UPDATE_DISPLAYED",
            payload: text.slice(0, indexRef.current),
          });
          gsap.delayedCall(speed / 1000, type);
        } else {
          dispatch({ type: "SET_DONE" });
        }
      };
      type();
    });

    return () => {
      cancelledRef.current = true;
      delayTimer.kill();
    };
  }, [text, speed, startDelay, enabled]);

  useEffect(() => {
    if (state.isDone) {
      onComplete?.();
    }
  }, [state.isDone, onComplete]);

  return { displayed: state.displayed, isDone: state.isDone };
}
